// client/src/pages/clientPortal/PortalDashboardPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientPortalApiClient } from '../../hooks/useClientPortalApiClient';
import CollapsibleCard, { ExpandCollapseAll, CardHeading } from '../../components/shared/CollapsibleCard';
import { countSummary, transactionsSummary } from '../../utils/collapsibleCards';

const PortalDashboardPage = () => {
    const apiClient = useClientPortalApiClient();
    const [transactions, setTransactions] = useState([]);
    const [docs, setDocs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const navigate = useNavigate();
    useEffect(() => {
        apiClient.get('/api/client-portal/transactions')
            .then(setTransactions)
            .catch(err => {
                // A website visitor's account opens the property features only.
                if (/invited you as a client/.test(err.message || '')) { navigate('/client-portal/my-properties', { replace: true }); return; }
                setError(err.message || 'Failed to load your transactions.');
            })
            .finally(() => setIsLoading(false));
        // Intake paperwork exists before there is a deal, which is when a client
        // most wants to read what they signed.
        apiClient.get('/api/client-portal/my-documents').then(setDocs).catch(() => setDocs([]));
    }, [apiClient, navigate]);

    const downloadDoc = async (d) => {
        try {
            const response = await apiClient.get(`/api/client-portal/my-documents/${d.document_id}/download`, { responseType: 'blob' });
            const fileURL = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = d.document_name || 'document';
            link.click();
            URL.revokeObjectURL(fileURL);
        } catch (err) {
            alert(err.message || 'Could not download the document.');
        }
    };

    if (isLoading) return <p className="text-slate-500">Loading...</p>;
    if (error) return <p className="text-red-500">{error}</p>;

    return (
        <div>
            <ExpandCollapseAll className="mb-3" />
            {docs.length > 0 && (
                <CollapsibleCard id="client-documents" title="Your Documents" summary={countSummary(docs.length, docs.length === 1 ? 'document' : 'documents')} className="mb-3">
                <div className="mb-8">
                    <CardHeading as="h1" className="mb-3 text-2xl font-bold text-slate-800">Your Documents</CardHeading>
                    <div className="divide-y divide-slate-100 rounded-lg bg-white shadow">
                        {docs.map((d) => (
                            // Fetched with the portal login and saved as a file: a plain link sends
                            // no Authorization header, so it only ever opened an error page.
                            <a key={d.document_id}
                               href={`/api/client-portal/my-documents/${d.document_id}/download`}
                               onClick={(e) => { e.preventDefault(); downloadDoc(d); }}
                               className="flex items-center justify-between px-6 py-4 hover:bg-slate-50">
                                <span>
                                    <span className="block font-medium text-slate-800">{d.document_name}</span>
                                    <span className="block text-xs text-slate-500">
                                        {d.document_type}
                                        {d.upload_timestamp ? ` · ${new Date(d.upload_timestamp).toLocaleDateString()}` : ''}
                                    </span>
                                </span>
                                <span className="text-sm text-indigo-600">Download</span>
                            </a>
                        ))}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                        Questions about any of these go to your designated agent. Documents needing signature are
                        sent separately for signing.
                    </p>
                </div>
                </CollapsibleCard>
            )}

            <CollapsibleCard id="client-transactions" title="Your Transactions" summary={transactionsSummary(transactions)} defaultOpen>
            <CardHeading as="h1" className="text-2xl font-bold text-slate-800 mb-6">Your Transactions</CardHeading>
            {transactions.length === 0 ? (
                <div className="bg-white p-8 rounded-lg shadow text-slate-500">
                    You don't have any transactions on file yet. Your agent will be in touch.
                </div>
            ) : (
                <div className="space-y-4">
                    {transactions.map(t => (
                        <Link
                            key={t.transaction_id}
                            to={`/client-portal/transactions/${t.transaction_id}`}
                            className="block bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-800">{t.street_address}</h2>
                                    <p className="text-slate-500 text-sm">{t.city}, {t.state} {t.zip_code} — You are the {t.client_role}</p>
                                </div>
                                <span className="px-3 py-1 text-sm font-semibold rounded-full bg-indigo-100 text-indigo-800">{t.status}</span>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
            </CollapsibleCard>
        </div>
    );
};

export default PortalDashboardPage;
